// Recent Searches helper (localStorage)
// Stores last N catalog/home filter snapshots and provides simple CRUD + labels.
// Loaded globally so both home and catalog can use it.
(function () {
    'use strict';

    const STORAGE_KEY = 'catalog_recent_searches_v1';
    const MAX_ITEMS = 20;

    function safeJsonParse(str, fallback) {
        try { return JSON.parse(str); } catch (e) { return fallback; }
    }

    function isPlainObject(v) {
        return v !== null && typeof v === 'object' && !Array.isArray(v);
    }

    function cleanStr(v) {
        if (v === null || v === undefined) return '';
        return String(v).trim();
    }

    function cleanNumStr(v) {
        const s = cleanStr(v);
        if (!s) return '';
        // Keep digits only
        const digits = s.replace(/[^\d]/g, '');
        return digits || '';
    }

    function canonicalFilterValue(filters, idKey, nameKey) {
        const id = cleanStr(filters[idKey]);
        if (id) return { kind: 'id', value: id };
        const name = cleanStr(filters[nameKey]);
        if (name) return { kind: 'name', value: name.toLowerCase() };
        return { kind: '', value: '' };
    }

    // Build a stable key for dedupe based on meaningful filters only.
    function buildKey(filters) {
        const f = isPlainObject(filters) ? filters : {};

        const makeRef = canonicalFilterValue(f, 'make_id', 'make_name');
        const makeAlt = canonicalFilterValue(f, 'make_id', 'make'); // home uses make
        const make = makeRef.value || makeAlt.value;

        const modelRef = canonicalFilterValue(f, 'model_id', 'model_name');
        const modelAlt = canonicalFilterValue(f, 'model_id', 'model');
        const model = modelRef.value || modelAlt.value;

        const out = {
            category: cleanStr(f.category || f.v_type || ''),
            seller_type: cleanStr(f.seller_type || ''),
            sort: cleanStr(f.sort || ''),
            make: make,
            model: model,
            price_from: cleanNumStr(f.price_from),
            price_to: cleanNumStr(f.price_to),
            year_from: cleanNumStr(f.year_from),
            year_to: cleanNumStr(f.year_to),
            engine_type: cleanStr(f.engine_type || ''),
            engine_volume_from: cleanStr(f.engine_volume_from || f.engine_volume || ''),
            engine_volume_to: cleanStr(f.engine_volume_to || ''),
            power_from: cleanStr(f.power_from || f.power || ''),
            power_to: cleanStr(f.power_to || ''),
            gearbox: cleanStr(f.gearbox || ''),
            body_type: cleanStr(f.body_type || ''),
            seats: cleanStr(f.seats || ''),
            color: cleanStr(f.color || ''),
        };

        // Remove empties
        Object.keys(out).forEach((k) => {
            if (out[k] === '') delete out[k];
        });

        // Stable stringify (sorted keys)
        const stable = {};
        Object.keys(out).sort().forEach((k) => { stable[k] = out[k]; });
        return JSON.stringify(stable);
    }

    // Build a "base" key that represents the same search being refined (make + model + category).
    // This allows us to UPDATE the last search entry while user adds extra filters (kw/year/body type...),
    // instead of creating multiple near-duplicate entries.
    function buildBaseKey(filters) {
        const f = isPlainObject(filters) ? filters : {};

        const makeRef = canonicalFilterValue(f, 'make_id', 'make_name');
        const makeAlt = canonicalFilterValue(f, 'make_id', 'make'); // home uses make
        const make = makeRef.value || makeAlt.value;

        const modelRef = canonicalFilterValue(f, 'model_id', 'model_name');
        const modelAlt = canonicalFilterValue(f, 'model_id', 'model');
        const model = modelRef.value || modelAlt.value;

        const out = {
            category: cleanStr(f.category || f.v_type || ''),
            make: make,
            model: model,
        };

        Object.keys(out).forEach((k) => {
            if (out[k] === '') delete out[k];
        });

        const stable = {};
        Object.keys(out).sort().forEach((k) => { stable[k] = out[k]; });
        return JSON.stringify(stable);
    }

    function buildLabel(filters) {
        const f = isPlainObject(filters) ? filters : {};

        const make = cleanStr(f.make_name || f.make || '');
        const model = cleanStr(f.model_name || f.model || '');
        const category = cleanStr(f.category || f.v_type || '');

        let title = '';
        if (make && model) title = `${make} ${model}`;
        else if (make) title = make;
        else if (category) title = category;
        else title = 'Search';

        const meta = [];

        const yf = cleanNumStr(f.year_from);
        const yt = cleanNumStr(f.year_to);
        if (yf || yt) meta.push(`${yf || '…'}–${yt || '…'}`);

        const pf = cleanNumStr(f.price_from);
        const pt = cleanNumStr(f.price_to);
        if (pf || pt) meta.push(`€${pf || '…'}–${pt || '…'}`);

        const seller = cleanStr(f.seller_type || '');
        if (seller && seller !== 'all') meta.push(seller);

        return meta.length ? `${title} · ${meta.join(' · ')}` : title;
    }

    function buildTooltip(filters) {
        const f = isPlainObject(filters) ? filters : {};
        const lines = [];

        // Tooltip is best-effort and uses the already translated UI texts from the page,
        // so it automatically matches the current language (LV/RU/EN) without hardcoding.

        function cssEscape(value) {
            const s = cleanStr(value);
            // CSS.escape is not supported in some older browsers; fallback is "good enough" for our values.
            if (typeof window !== 'undefined' && window.CSS && typeof window.CSS.escape === 'function') {
                return window.CSS.escape(s);
            }
            return s.replace(/["\\]/g, '\\$&');
        }

        function textFrom(el) {
            if (!el) return '';
            return cleanStr(el.textContent || '');
        }

        function optionTextBySelectName(selectName, value) {
            const v = cleanStr(value);
            if (!selectName || !v) return '';
            const selects = (typeof document !== 'undefined')
                ? document.querySelectorAll(`select[name="${cssEscape(selectName)}"]`)
                : [];
            for (const sel of selects) {
                // Prefer Selectize option text if available
                const sz = sel && sel.selectize;
                if (sz && sz.options && sz.options[v] && sz.options[v].text) {
                    const t = cleanStr(sz.options[v].text);
                    if (t) return t;
                }
                // Fallback to DOM option text
                try {
                    const opt = sel.querySelector(`option[value="${cssEscape(v)}"]`);
                    const t = textFrom(opt);
                    if (t) return t;
                } catch (e) {}
            }
            return '';
        }

        function radioLabelTextByNameValue(inputName, value) {
            const v = cleanStr(value);
            if (!inputName || !v) return '';
            const inputs = (typeof document !== 'undefined')
                ? document.querySelectorAll(`input[name="${cssEscape(inputName)}"][value="${cssEscape(v)}"]`)
                : [];
            for (const input of inputs) {
                // Common structure in this project: input inside ".checkbox-single" with ".checkbox-title" in label
                const wrap = input.closest ? input.closest('.checkbox-single') : null;
                if (wrap) {
                    const titleEl = wrap.querySelector('.checkbox-title');
                    const t = textFrom(titleEl);
                    if (t) return t;
                }
                // Fallback: label[for=id]
                const id = input.id;
                if (id) {
                    const label = document.querySelector(`label[for="${cssEscape(id)}"]`);
                    const titleEl = label ? label.querySelector('.checkbox-title') : null;
                    const t1 = textFrom(titleEl) || textFrom(label);
                    if (t1) return t1;
                }
            }
            return '';
        }

        function categoryLabel(categoryValue) {
            const c = cleanStr(categoryValue);
            if (!c || typeof document === 'undefined') return c;
            const selectors = [
                `.catalog-filters-non-dropdown-link[data-category="${cssEscape(c)}"] span`,
                `.catalog-filters-non-dropdown-link[data-category="${cssEscape(c)}"]`,
                `.home-filters-category-link[data-category="${cssEscape(c)}"] span`,
                `.home-filters-category-link[data-category="${cssEscape(c)}"]`,
            ];
            for (const sel of selectors) {
                const el = document.querySelector(sel);
                const t = textFrom(el);
                if (t) return t;
            }
            return c;
        }

        function sellerTypeLabel(sellerType) {
            const s = cleanStr(sellerType);
            if (!s) return '';

            // Catalog seller type radios
            const fromCatalog = radioLabelTextByNameValue('form-catalog-seller-type', s);
            if (fromCatalog) return fromCatalog;

            // Home page uses posts-type radios: all/own
            if (s === 'carbuy') {
                const fromHome = radioLabelTextByNameValue('posts-type', 'own');
                if (fromHome) return fromHome;
            }
            if (s === 'all') {
                const fromHome = radioLabelTextByNameValue('posts-type', 'all');
                if (fromHome) return fromHome;
            }

            return s;
        }

        // Resolve values with translations from UI where possible
        const categoryRaw = cleanStr(f.category || f.v_type || '');
        const category = categoryLabel(categoryRaw);
        if (category) lines.push(`Kategorija: ${category}`);

        const make = cleanStr(f.make_name || f.make || '');
        if (make) lines.push(`Marka: ${make}`);

        const model = cleanStr(f.model_name || f.model || '');
        if (model) lines.push(`Modelis: ${model}`);

        const bodyRaw = cleanStr(f.body_type || '');
        const body = optionTextBySelectName('form-catalog-body-type', bodyRaw)
            || optionTextBySelectName('form-home-filters-body-type', bodyRaw)
            || bodyRaw;
        if (body) lines.push(`Virsbūve: ${body}`);

        const fuelRaw = cleanStr(f.engine_type || '');
        const fuel = optionTextBySelectName('form-catalog-engine-type', fuelRaw)
            || optionTextBySelectName('form-home-filters-fuel', fuelRaw)
            || fuelRaw;
        if (fuel) lines.push(`Degviela: ${fuel}`);

        const gearboxRaw = cleanStr(f.gearbox || '');
        const gearbox = optionTextBySelectName('form-catalog-gearbox', gearboxRaw)
            || optionTextBySelectName('form-home-filters-gearbox', gearboxRaw)
            || gearboxRaw;
        if (gearbox) lines.push(`Ātrumkārba: ${gearbox}`);

        const seatsRaw = cleanStr(f.seats || '');
        const seats = optionTextBySelectName('form-catalog-seats', seatsRaw)
            || optionTextBySelectName('form-home-filters-seats', seatsRaw)
            || seatsRaw;
        if (seats) lines.push(`Sēdvietas: ${seats}`);

        const colorRaw = cleanStr(f.color || '');
        const color = optionTextBySelectName('form-catalog-color', colorRaw) || colorRaw;
        if (color) lines.push(`Krāsa: ${color}`);

        const yf = cleanNumStr(f.year_from);
        const yt = cleanNumStr(f.year_to);
        if (yf || yt) lines.push(`Gads: ${yf || '…'}–${yt || '…'}`);

        const pf = cleanNumStr(f.price_from);
        const pt = cleanNumStr(f.price_to);
        if (pf || pt) lines.push(`Cena: €${pf || '…'}–€${pt || '…'}`);

        const evf = cleanStr(f.engine_volume_from || f.engine_volume || '');
        const evt = cleanStr(f.engine_volume_to || '');
        if (evf || evt) lines.push(`Tilpums: ${evf || '…'}–${evt || '…'} L`);

        const pof = cleanStr(f.power_from || f.power || '');
        const pot = cleanStr(f.power_to || '');
        if (pof || pot) lines.push(`Jauda: ${pof || '…'}–${pot || '…'} kW`);

        const sellerRaw = cleanStr(f.seller_type || '');
        const seller = sellerTypeLabel(sellerRaw);
        if (sellerRaw && sellerRaw !== 'all') lines.push(`Pārdevējs: ${seller}`);

        const sortRaw = cleanStr(f.sort || '');
        const sort = optionTextBySelectName('form-catalog-sort', sortRaw) || sortRaw;
        if (sortRaw && sortRaw !== 'latest') lines.push(`Kārtošana: ${sort}`);

        return lines.join('\n');
    }

    function readAll() {
        try {
            const raw = localStorage.getItem(STORAGE_KEY);
            const list = safeJsonParse(raw, []);
            return Array.isArray(list) ? list.filter(isPlainObject) : [];
        } catch (e) {
            return [];
        }
    }

    function writeAll(list) {
        try {
            localStorage.setItem(STORAGE_KEY, JSON.stringify(list));
        } catch (e) {
            // ignore quota / privacy mode
        }
    }

    function upsert(filters) {
        if (!isPlainObject(filters)) return null;

        const key = buildKey(filters);
        if (!key || key === '{}' || key === '""') return null;

        const baseKey = buildBaseKey(filters);

        const label = buildLabel(filters);
        const now = Date.now();

        const list = readAll();

        const entry = {
            key,
            ts: now,
            label,
            // Store a snapshot of filters as-is (keeps make/model names when available)
            filters: filters,
        };

        // Remove exact duplicate (same full key)
        for (let i = list.length - 1; i >= 0; i--) {
            if (list[i] && list[i].key === key) list.splice(i, 1);
        }

        // Remove any older entries for the same "base search" (make+model+category),
        // so refinements update the existing chip instead of adding a new one.
        if (baseKey && baseKey !== '{}' && baseKey !== '""') {
            for (let i = list.length - 1; i >= 0; i--) {
                const it = list[i];
                if (!it || !it.filters) continue;
                if (buildBaseKey(it.filters) === baseKey) {
                    list.splice(i, 1);
                }
            }
        }

        list.unshift(entry);

        // Enforce max
        while (list.length > MAX_ITEMS) list.pop();

        writeAll(list);
        return entry;
    }

    function get(key) {
        const list = readAll();
        return list.find((x) => x && x.key === key) || null;
    }

    function remove(key) {
        const list = readAll();
        const next = list.filter((x) => x && x.key !== key);
        writeAll(next);
        return next;
    }

    function clear() {
        writeAll([]);
    }

    function list() {
        // Newest first (already stored that way)
        return readAll();
    }

    window.CarBuyRecentSearches = window.CarBuyRecentSearches || {
        key: buildKey,
        baseKey: buildBaseKey,
        label: buildLabel,
        tooltip: buildTooltip,
        upsert,
        list,
        get,
        remove,
        clear,
        STORAGE_KEY,
        MAX_ITEMS,
    };
})();

